import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { OrdenesService } from '../../utilidades/ordenes.service';
import {
  CONFIGURACION_FICHA_PREPARAR_ORDENES,
  CONFIGURACION_TABLA_PREPARAR_ORDENES,
} from './utilidades/preparar-ordenes.static';
import { deepCopy } from 'src/app/compartido/utilidades/objetos.utils';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import { ResponsiveService } from 'src/app/nucleo/utilidades/responsive.service';
import { SourceTablaOrdenes } from '../../utilidades/source-tabla-ordenes.class';
import { TablaAsincronaComponent } from 'src/app/compartido/tabla-asincrona/tabla-asincrona.component';
import { DomiciliosService } from 'src/app/domicilios/utilidades/domicilios.service';
import { DialogosService } from 'src/app/nucleo/utilidades/dialogos.service';
import { ConfiguracionDialogoVistaPrevia } from 'src/app/compartido/dialogos/dialogo-vista-previa/utilidades/dialogo-vista-previa.static';
import { PrepararOrdenComponent } from '../../componentes/preparar-orden/preparar-orden.component';
import { ActivatedRoute } from '@angular/router';
import {
  formatearFecha,
  stringAleatorio,
  valorNumérico,
} from 'src/app/compartido/utilidades/valores.utils';
import { obtenerFiltro } from 'src/app/compartido/tabla/utilidades/tabla.utils';
import { FormulariosService } from 'src/app/nucleo/utilidades/formularios.service';
import { Parametros } from 'src/app/compartido/utilidades/compartido.models';
import { WebSocketsService } from 'src/app/nucleo/utilidades/websockets.service';
import { environment } from 'src/environments/environment';
import { AutenticacionService } from 'src/app/nucleo/autenticacion/autenticacion.service';

@Component({
  selector: 'ordenes-preparar-ordenes',
  templateUrl: './preparar-ordenes.component.html',
  styleUrls: ['./preparar-ordenes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PrepararOrdenesComponent implements OnInit, OnDestroy {
  /* Suscripción */
  private _suscripción = new Subscription();

  /* Configuración de la ficha */
  protected configFicha = deepCopy(CONFIGURACION_FICHA_PREPARAR_ORDENES);

  /* ID de la organización */
  protected orgId: number | undefined;

  /* Tabla de Órdenes */
  protected configTabla = deepCopy(CONFIGURACION_TABLA_PREPARAR_ORDENES);
  protected dataSource: SourceTablaOrdenes;
  @ViewChild('tabla') tabla!: TablaAsincronaComponent;

  /* Etiquetas a imprimir */
  protected etiquetas: number[] = [];

  /* Órdenes preparadas */
  protected preparadas = signal(0);

  /* Estado de carga */
  protected cargando = signal(true);

  /* Bloqueadas por otros usuarios */
  private _sesion: string = stringAleatorio(10);
  private _bloqueadas: number[] = [];

  constructor(
    private readonly _ordenesService: OrdenesService,
    private readonly _domiciliosService: DomiciliosService,
    private readonly _formulariosService: FormulariosService,
    private readonly _webSocketsService: WebSocketsService,
    private readonly _alertasService: AlertasService,
    private readonly _dialogosService: DialogosService,
    private readonly _autenticacionService: AutenticacionService,
    private readonly _route: ActivatedRoute,
    private readonly _cdr: ChangeDetectorRef,
    protected readonly responsiveService: ResponsiveService,
  ) {
    this.dataSource = new SourceTablaOrdenes(_ordenesService);
  }

  ngOnInit(): void {
    const filtros = this.configTabla.filtros!;

    // Agregar observables a los filtros de la tablas
    this._formulariosService.agregarValorIn(filtros, [
      'organizacion',
      'origen_null',
    ]);

    // Escuchar query params
    firstValueFrom(this._route.queryParams).then((params) => {
      this.orgId = valorNumérico(params['organizacion']);
      if (this.orgId === undefined)
        throw new ErrorFrontend('Organización no proporcionada');
      const filtroOrg = obtenerFiltro(filtros, 'organizacion')!;
      filtroOrg.valorIn!.next(this.orgId);
      this._autenticacionService.usuario$.then((usuario) => {
        const sedeId = usuario?.empleado?.sede?.id;
        if (sedeId) {
          const filtroSede = obtenerFiltro(filtros, 'origen_null')!;
          filtroSede.valorIn!.next(sedeId);
        }
        this._cargarPreparadas$();
      });
    });

    // Escuchar websocket de órdenes en preparación
    this._webSocketsService
      .crearSocket$(
        'preparacion',
        environment.wsUrl + '/ordenes/preparacion/?sesion=' + this._sesion,
      )
      .then(() => {
        const escucharPreparaciones = this._webSocketsService
          .escucharMensajes$('preparacion')
          .subscribe((bloqueadas: { referencia: string; orden: number }[]) => {
            this._bloqueadas = bloqueadas.map((bloqueada) => bloqueada.orden);
            this.configTabla.acciones![0].ocultar = (fila) =>
              this._bloqueadas.includes(fila['id']);
            console.log(this._bloqueadas);
            this._cdr.markForCheck();
          });
        this._suscripción.add(escucharPreparaciones);
        this._webSocketsService.enviarMensaje('preparacion', {
          tipo: 'actualizar',
        });
      });
  }

  ngOnDestroy(): void {
    this._webSocketsService.cerrarSocket('preparacion');
    this._suscripción.unsubscribe();
  }

  /**
   * Carga las órdenes preparadas.
   * @autor Juan Corral
   */
  private async _cargarPreparadas$(): Promise<void> {
    this.cargando.set(true);

    const filtros: Parametros = {
      organizacion: this.orgId!,
      fecha_preparacion: formatearFecha(new Date()),
      autor_preparacion: (await this._autenticacionService.usuario$)!.id,
    };

    this._ordenesService.obtenerOrdenes$(filtros).then((ordenes) => {
      this.preparadas.set(ordenes.count);
      this.cargando.set(false);
    });
  }

  /**
   * Abre el dialogo de preparar orden.
   * @param {number} id - El id de la orden a preparar.
   * @autor Juan Corral
   */
  protected async prepararOrden$(id: number): Promise<void> {
    // Verificar si la orden está bloqueada
    if (this._bloqueadas.includes(id)) {
      this._alertasService.alertarError(
        'La orden está siendo preparada por otro usuario',
      );
      return;
    }

    // Bloquear orden
    this._webSocketsService.enviarMensaje('preparacion', {
      tipo: 'bloquear',
      orden: id,
    });

    // Abrir dialogo
    const config: ConfiguracionDialogoVistaPrevia = {
      componente: PrepararOrdenComponent,
      inputs: { idOrden: id },
      output: 'preparada',
      botonesSuperpuestos: true,
      botonCerrar: true,
    };
    const preparada = await this._dialogosService.abrirVistaPrevia$(config);

    // Desbloquear orden
    this._webSocketsService.enviarMensaje('preparacion', {
      tipo: 'desbloquear',
      orden: id,
    });

    // Actualizar listas
    if (preparada !== null && preparada !== undefined) {
      if (preparada) this.etiquetas.push(id);
      this.tabla.cargarPagina();
      this._cargarPreparadas$();
    }
  }

  /**
   * Genera las etiquetas de las órdenes preparadas.
   * @autor Juan Corral
   */
  protected generarEtiquetas(): void {
    const cantidad = this.etiquetas.length;
    if (cantidad === 0) return;
    const s = cantidad > 1 ? 's' : '';

    this._dialogosService
      .abrirDialogo$(
        `¿Descargar ${cantidad} etiqueta${s} y despejar el lote?`,
        `Se descargará un archivo PDF con la${s} etiqueta${s} del lote actual y se reiniciará el conteo.`,
        'Confirmar',
        'Cancelar',
      )
      .then(async (confirmado: boolean) => {
        if (!confirmado) return;

        this.cargando.set(true);
        this._alertasService.alertar(
          `Generando el PDF, esto puede tardar unos segundos...`,
        );

        const filtros: Parametros = { ids: this.etiquetas.join(',') };
        this._ordenesService
          .descargarEtiquetas$(filtros)
          .then(() => {
            this._alertasService.alertarExito(
              'El archivo ha sido descargado correctamente',
            );
            this.cargando.set(false);
            this.etiquetas = [];
          })
          .catch((error: ErrorFrontend) => {
            this._alertasService.alertarError('Error: ' + error.message);
            this.cargando.set(false);
          });
      });
  }
}
